import {
  getAllLinkingRequests,
  setLinkingRequest
} from '#~/agent/bridge/auto-linking/persistence.js';
import {
  convertSystemInfoToIrisEnums,
  enhanceDiscoveredService,
  normalizeHostname,
  type DiscoveredService,
  type SystemInfo
} from '#~/agent/discovery/utils.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { fetchRoomIdForAgentId, type LinkRequest } from '#~/api/agent/index.js';
import { createBridgeDiscoveryLinkingAuthRequest } from '#~/api/bridge-discovery/registry.js';
import { useAuth0 } from '#~/auth/index.js';
import { config } from '#~/config/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { ElectronAPI } from '#~/electron/api.js';
import { isElectronApp } from '#~/electron/utils.js';
import { evaluateBridgeLink, PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useRoom } from '#~/room/hooks.js';
import { useUser } from '#~/user/hooks.js';
import { Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { usePrompt, type UsePrompt } from '@studio/design-system/prompt';
import { Spinner } from '@studio/design-system/spinner';
import type { Agent } from '@studio/models/agent';
import type { RoomRecord } from '@studio/models/room';
import { useInterval, useLatestFn, useMount } from '@studio/react-utils/hooks';
import { isNotEmpty } from '@tryiris-ai/js-types';
import { clsx } from 'cn';
import { pick } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import {
  useConnectToMDNSService,
  useDirectLinkAgent,
  useGetMachineAgentId,
  useLinkAgent,
  useLinkAgentDetails,
  useRoomAgent,
  useUnlinkAgent
} from '../hooks.js';
import { getImage, useDeviceImage, type AgentSystemInfo } from './device-img.js';

// Simple store that keeps track of bridge discovery linking request ownership
export const useBridgeDiscoveryLinkingStore = create<{
  ownsBridge: boolean;
  requestInFlight: boolean;
  isPreApprovedForUnlink: boolean;
  setOwnsBridge: (ownsBridge: boolean) => void;
  setRequestInFlight: (requestInFlight: boolean) => void;
  setPreApprovedForUnlink: (isPreApprovedForUnlink: boolean) => void;
}>()((set) => ({
  ownsBridge: false,
  requestInFlight: false,
  isPreApprovedForUnlink: false,
  setOwnsBridge: (ownsBridge) => set({ ownsBridge }),
  setRequestInFlight: (requestInFlight) => set({ requestInFlight }),
  setPreApprovedForUnlink: (isPreApprovedForUnlink) => set({ isPreApprovedForUnlink })
}));

export type DirectLinkRequestDetails = {
  statusDetails?: {
    linked?: boolean;
    bridgeId?: Agent['id'];
    error?: {
      message?: string;
      code?: number;
    };
  };
  linkDetails: {
    requestId: LinkRequest['id'];
    bridgeId: Agent['id'];
    username?: string;
    password?: string;
    token: string;
    lockoutStatus?: {
      isLocked: boolean;
      unlockTimeSeconds: number;
    };
    error?: {
      message?: string;
      code?: number;
    };
  };
};

/**
 * `MDNSServiceEntry` component to display discovered mDNS services
 */
type MDNSServiceEntryProps = {
  service: DiscoveredService;
  isPtzoptics: boolean;
  handleConnect: () => void;
  handleCancelLinkRequest: () => void;
  isConnecting?: boolean;
  error: Error | null;
};
/**
 * @internal `MDNSServiceEntry` component to display discovered mDNS services
 */
const MDNSServiceEntry = ({
  service,
  isPtzoptics,
  handleConnect,
  handleCancelLinkRequest,
  isConnecting = false,
  error
}: MDNSServiceEntryProps) => {
  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE, {
    defaultValue: false
  });

  const isIrisService = service.type?.includes('_iris') || service.data?.service === 'iris';
  const normalizedName = normalizeHostname(service.host);
  const displayName =
    service?.data?.computerName ||
    normalizedName ||
    (isIrisService ? 'Iris Bridge' : 'Unknown Device');

  const { ownsBridge } = useBridgeDiscoveryLinkingStore();

  // Use unified device image logic
  const deviceImageSrc = useDeviceImage(service);

  return (
    <div
      className={clsx('rounded-4 p-12', {
        'border-1 border-error': !!error,
        'border-1 border-primary': isConnecting,
        'bg-base-200': isPtzoptics,
        'bg-base-300': !isPtzoptics
      })}
    >
      <div className='flex items-center'>
        <div className='flex flex-grow items-center gap-8'>
          <img src={deviceImageSrc} className='w-40' data-stag='bridge.discovery.device_image' />
          <div>{displayName}</div>
        </div>
        {isBridgeEnabled && !isConnecting && (
          <Button variant='secondary' onClick={handleConnect} className='flex items-center gap-4'>
            Link Bridge
          </Button>
        )}
        {isConnecting && ownsBridge && <Spinner className='h-24 w-24 text-primary' />}
        {isConnecting && !ownsBridge && (
          <Button
            variant='danger'
            onClick={handleCancelLinkRequest}
            className='flex items-center gap-4'
          >
            Cancel
          </Button>
        )}
      </div>
      {!!error && (
        <div className='mt-12 flex items-start gap-8'>
          <i className='i-ion-warning-outline text-lg text-error' />
          <p className='text-muted'>{error?.message || 'Error connecting to service'}</p>
        </div>
      )}
      {isConnecting && (
        <div className='mt-12 flex items-start gap-8'>
          <div className='i-ion-alert-circle-outline -mt-10 text-3xl text-primary' />
          <p className='text-muted'>
            Requesting approval from Bridge owner to link the Bridge. Once approved, your Bridge
            will finish linking.
          </p>
        </div>
      )}
    </div>
  );
};

export type DeviceEntryProps = {
  agentId: Agent['id'];
  disabled?: boolean;
  thisComputer?: boolean;
  error?: string | undefined;
  name: string;
  linking?: boolean;
  onLink?: () => void;
  state: 'assigned' | 'linked' | 'unlinked';
  isPtzoptics?: boolean;
  selfDiscoerySystemInfo?: SystemInfo | null;
  agentSystemInfo?: Record<Agent['id'], AgentSystemInfo> | null;
};
/* @internal `DeviceEntry` component*/
const DeviceEntry = ({
  agentId,
  disabled,
  thisComputer,
  name,
  linking,
  onLink,
  error,
  state,
  isPtzoptics,
  selfDiscoerySystemInfo,
  agentSystemInfo
}: DeviceEntryProps) => {
  const deviceDetails = useMemo(
    () =>
      isNotEmpty(agentSystemInfo)
        ? agentSystemInfo?.[agentId]
          ? { agentId, systemInfo: agentSystemInfo[agentId] }
          : { agentId }
        : { agentId },
    [agentId, agentSystemInfo]
  );

  const deviceImageFromHook = useDeviceImage(deviceDetails);

  // Prefer explicit self-discovery image when agent-level systemInfo is not present
  const { machineType, operatingSystem } = convertSystemInfoToIrisEnums(
    (selfDiscoerySystemInfo ?? {}) as SystemInfo
  );

  const deviceImageSrc =
    'systemInfo' in deviceDetails && deviceDetails.systemInfo
      ? deviceImageFromHook
      : getImage(operatingSystem, machineType);

  return (
    <div
      className={clsx('rounded-4 p-12', {
        'border-1 border-error': !!error,
        'bg-base-200': isPtzoptics,
        'bg-base-300': !isPtzoptics
      })}
    >
      <div className='flex items-center'>
        <div className='flex flex-grow items-center gap-8'>
          <img src={deviceImageSrc} className='w-40' data-stag='bridge.device_image.unified' />
          <div>
            <div>{name}</div>
            {thisComputer && (
              <div className={clsx('mt-4 text-sm', isPtzoptics ? 'text-white' : 'text-secondary')}>
                (This computer)
              </div>
            )}
          </div>
        </div>
        {state === 'unlinked' && (
          <Button
            variant='secondary'
            disabled={linking || disabled}
            onClick={onLink}
            className='flex items-center gap-4'
          >
            {linking ? (
              <>
                <Spinner />
                <span>Linking...</span>
              </>
            ) : error ? (
              `Try Again`
            ) : (
              'Link Bridge'
            )}
          </Button>
        )}
        {state === 'linked' && <div className='text-sm font-medium text-success'>Linked</div>}
      </div>
      {!!error && (
        <div className='mt-12 flex items-start gap-8'>
          <i className='i-ion-warning-outline text-lg text-error' />
          <p className='text-muted'>{error}</p>
        </div>
      )}
    </div>
  );
};

export type SearchDevicesProps = {
  agents: { id: Agent['id']; name: Agent['name']; status: Agent['status'] }[];
  selfDiscoerySystemInfo: SystemInfo | null;
  agentSystemInfo: Record<Agent['id'], AgentSystemInfo> | null;
  isPtzoptics: boolean;
  machineName: string;
  discoveredServices?: DiscoveredService[];
  roomAgentId: Agent['id'] | undefined;
  loadSystemInfo: boolean;
};
/**
 * `SearchDevices` component for the bridge tray.
 *
 * @description A component that searches for devices.
 */
export const SearchDevices = ({
  agents,
  selfDiscoerySystemInfo,
  agentSystemInfo,
  isPtzoptics,
  machineName,
  discoveredServices = [],
  roomAgentId,
  loadSystemInfo
}: SearchDevicesProps) => {
  const user = useUser((µ) => pick(µ, ['email', 'firstName', 'lastName', 'id']), shallow);
  const { getAccessTokenSilently } = useAuth0();
  const studioName = useRoom((µ) => µ.name);
  const prompt = usePrompt();
  const { track } = useAnalytics();
  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE, {
    defaultValue: false
  });
  const { enabled: isMultiBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.MULTIBRIDGE_FEATURE, {
    defaultValue: false
  });

  const { setOwnsBridge, setRequestInFlight, requestInFlight } = useBridgeDiscoveryLinkingStore();

  const {
    agent: machineAgent,
    loading: agentLoading,
    error: linkAgentError,
    reload
  } = useLinkAgentDetails();

  const {
    loading: linking,
    linkBridge: linkBridgeElectron,
    error: linkError
  } = useLinkAgent((linked) => {
    if (!linked) {
      if (isElectronApp()) {
        ElectronAPI.onReloadStudio();
      }
      window.location.reload();
      return;
    }

    // Timeout needed for intercom event for first link to succeed
    setTimeout(() => {
      window.location.reload();
    }, 3_000);
  });

  const { linkBridgeDirect } = useDirectLinkAgent(
    async (linked) => {
      if (!linked) {
        if (isElectronApp()) {
          ElectronAPI.onReloadStudio();
        }
        window.location.reload();
        return;
      }

      await setLinkingRequest(null, connectingServiceHost!);
      setConnectingServiceHost(null);

      // Always reload for successful linking to ensure UI components refresh
      // This fixes the issue where embedded cameras don't appear in source tray after re-linking
      if (isElectronApp()) {
        ElectronAPI.onReloadStudio();
      }
      window.location.reload();
    },
    async (error) => {
      setConnectingServiceHost(null);
      await setLinkingRequest(null, connectingServiceHost!);
      setOwnsBridge(false);
      setServiceErrors((prev) => ({
        ...prev,
        // @ts-ignore
        [connectingServiceHost ?? '']: new Error(String(error?.cause?.message ?? error?.message))
      }));
      console.error({ ___directLinkError: error });
    }
  );

  const reloadIrisQueryDevices = () => {
    ElectronAPI.mDNS.queryIris();
  };

  const { loading: unlinking, unlinkBridge } = useUnlinkAgent();
  const roomAgent = useRoomAgent(roomAgentId);
  const bridgeLinkDecision = evaluateBridgeLink(
    config.vendor,
    agents.length > 0,
    isBridgeEnabled,
    isMultiBridgeEnabled
  );
  const { agentId: machineAgentId, loading } = useGetMachineAgentId();

  // Use our custom hook for connecting to mDNS services
  const { requestDirectLinkToAgentDetails } = useConnectToMDNSService();
  const [connectingServiceHost, setConnectingServiceHost] = useState<string | null>(null);
  const [serviceErrors, setServiceErrors] = useState<Record<string, Error | null>>({});

  // Function to load persistent linking state for bridge requests
  const loadLinkingState = async () => {
    const allRequests = await getAllLinkingRequests();

    if (!allRequests) {
      return;
    }

    for (const [serviceHost, request] of Object.entries(allRequests)) {
      const isExpired = Date.now() - request.requestedAt > 60000;
      if (!isExpired) {
        setConnectingServiceHost(serviceHost);
      } else {
        // Clear expired request
        await setLinkingRequest(null, serviceHost);
      }
    }
  };

  // Add effect to load persistent linking state
  useMount(() => {
    loadLinkingState();
  });

  // this will continually 'poll' the current state of the persistent layer
  const interval = useInterval(
    () => {
      loadLinkingState();
    },
    { period: 777, start: !!connectingServiceHost }
  );

  useEffect(() => {
    if (!connectingServiceHost) {
      interval.stop();
    }
  }, [connectingServiceHost]);

  const [rooms, _] = useDepot((µ) => [µ.roomRecords, µ.produce], shallow);

  // Handler for connecting to a discovered mDNS service
  const handleConnectToService = useLatestFn(async (service: DiscoveredService) => {
    try {
      // The UI gate is mirrored at the action boundary so stale rendered state
      // or a direct callback cannot start an additional-Bridge request.
      if (!bridgeLinkDecision.allowed) {
        console.warn('[BridgeLink] Link request denied', {
          reason: bridgeLinkDecision.reason,
          vendor: config.vendor,
          hasExistingBridge: agents.length > 0
        });
        return;
      }

      if (requestInFlight) {
        // Potentially show alert here letting the user know we only currently
        // support one linking request at a time
        return;
      }

      let needsUnlink = await checkCanAutoUnlink(
        service.data?.agentId,
        rooms,
        await getAccessTokenSilently()
      );

      if (needsUnlink) {
        const result = await promptUserToUnlink(service, prompt);
        if (!result) {
          return;
        }
      }

      const normalizedHostname = normalizeHostname(service.host);
      // Clear any previous errors for this service
      setServiceErrors((prev) => ({
        ...prev,
        [service.host]: null
      }));

      setConnectingServiceHost(service.host);
      interval.start();

      // Save the linking request state
      await setLinkingRequest(
        {
          serviceHost: service.host,
          requestedAt: Date.now()
        },
        normalizedHostname
      );
      setRequestInFlight(true);

      // generate a UUID for the request
      let requestId = uuidv4();

      // 1) create linking record in database
      await createBridgeDiscoveryLinkingAuthRequest({
        token: await getAccessTokenSilently(),
        requestId
      });

      // 2) send gRPC call to create a linking request
      const { linkDetails } = (await requestDirectLinkToAgentDetails({
        service,
        user,
        studioName,
        bridgeLinkingRequestId: requestId,
        isPreApprovedForUnlink: needsUnlink
      })) as DirectLinkRequestDetails;

      track(EVENT_NAMES.SENDS_BRIDGE_LINK_REQUEST, EVENT_CATEGORIES.BRIDGE, {
        requestId: linkDetails.requestId,
        agentIdRequestingToLinkTo: linkDetails.bridgeId
      });

      if (linkDetails?.error) {
        if (linkDetails?.error?.message === 'Linking request cancelled') {
          // not *actually* an error, so don't set an error state
          return;
        }
        setConnectingServiceHost(null);
        await setLinkingRequest(null, normalizedHostname);
        interval.stop();
        setServiceErrors((prev) => ({
          ...prev,
          [service.host]: new Error(String(linkDetails?.error?.message))
        }));
        setOwnsBridge(false);
        return;
      }

      linkBridgeDirect({
        agentId: linkDetails.bridgeId,
        requestId: linkDetails.requestId,
        token: linkDetails.token
      });
    } catch (error) {
      // Store the error specifically for this service
      setServiceErrors((prev) => ({
        ...prev,
        [service.host]: error instanceof Error ? error : new Error(String(error))
      }));

      // Clear the linking request state on error
      await setLinkingRequest(null, normalizeHostname(service.host));
    } finally {
      setRequestInFlight(false);
    }
  });

  // Indicates whether or not the room/studio agent is the agent that is running on the user's machine
  const roomAgentSameAsMachine = (machineAgentId ?? machineAgent?.id) === roomAgent?.agent.id;

  const wrapperClassname = clsx('p-16 flex flex-col gap-8');
  const discoveredElementTitle = (
    <div className='text-sm font-medium text-[#CCD4E4]'>Discovered Devices</div>
  );

  if (loadSystemInfo) {
    return (
      <div className={wrapperClassname}>
        {discoveredElementTitle}
        <div className='mt-2 flex items-center justify-between rounded-4 bg-base-300 p-16'>
          <div className='text-sm font-demibold'>Loading system information...</div>
          <Spinner color={!isPtzoptics ? '#3664FF' : '#F47F00'} className='h-20 w-20' />
        </div>
      </div>
    );
  }

  /**
   * Loading state UI for device discovery.
   *
   * This conditional rendering handles the loading state when searching for devices:
   *
   * - Only shows when agents are still loading AND the room agent is different from the current machine
   * - Displays a "Searching for devices..." message with a spinner that changes color based on device type
   *   (blue for standard devices, orange for PTZOptics devices)
   * - If a room agent is already linked, it will still display that device below the loading indicator
   *   to show the user what's already connected while searching for new devices
   * - Uses the same wrapper and title styling as the main component for visual consistency
   */
  if (agentLoading && !roomAgentSameAsMachine) {
    return (
      <div className={wrapperClassname}>
        {discoveredElementTitle}
        <div className='mt-2 flex items-center justify-between rounded-4 bg-base-300 p-16'>
          <div className='text-sm font-demibold'>Searching for devices...</div>
          <Spinner color={!isPtzoptics ? '#3664FF' : '#F47F00'} className='h-20 w-20' />
        </div>
        {roomAgent && (
          <DeviceEntry
            agentId={roomAgent.agent.id}
            agentSystemInfo={agentSystemInfo}
            thisComputer={roomAgentSameAsMachine}
            name={(roomAgent?.agent?.name || undefined) ?? machineName}
            state='linked'
            isPtzoptics={isPtzoptics}
          />
        )}
      </div>
    );
  }

  const handleLinkBridgeElectron = async () => {
    if (machineAgentId !== '' && machineAgentId !== undefined) {
      // The machine agent is already linked to a bridge.
      prompt.confirm({
        title: 'Unlink Bridge',
        message: [
          'This Bridge is currently linked to another Studio.',
          'By continuing, it will be unlinked from the existing Studio and linked with this one. This change cannot be undone.'
        ].join('\n'),
        okay: {
          variant: 'danger',
          label: 'Link Bridge with this Studio'
        },
        cancel: {
          label: 'Cancel'
        },
        onConfirm: async (answer) => {
          if (answer) {
            track(EVENT_NAMES.OVERRIDES_BRIDGE, EVENT_CATEGORIES.BRIDGE, {
              machineAgentId: machineAgentId
            });
            unlinkBridge(machineAgentId)
              .then(() => {
                linkBridgeElectron();
              })
              .catch(() => {
                linkBridgeElectron();
              });
          }
        }
      });
    } else {
      linkBridgeElectron();
    }
  };

  const handleSearchAgain = () => {
    reload();
    reloadIrisQueryDevices();
  };

  const handleCancelLinkRequest = (service: DiscoveredService) => {
    ElectronAPI.mDNS.cancelLinkRequest(service);
    setConnectingServiceHost(null);
    setLinkingRequest(null, normalizeHostname(service.host));
  };

  const uniqueServicesMap = new Map<string, DiscoveredService>();

  for (const service of discoveredServices) {
    if (service.data?.agentId === 'null' || service.data?.agentId === null) {
      // @ts-ignore
      service.data!.agentId = undefined;
    }

    if (service.data?.machineId === 'null' || service.data?.machineId === null) {
      // @ts-ignore
      service.data!.machineId = undefined;
    }

    if (service.data?.agentId === machineAgent?.id) {
      continue;
    }

    if (agents.some((agent) => agent.id === service.data?.agentId)) {
      continue;
    }

    // Filter out services that are older than 30 seconds
    // @ts-ignore
    if ((service.data?.lastSeen ?? 0) + 30000 < Date.now()) {
      continue;
    }

    const key = service.data?.agentId ?? service.data?.machineId ?? service.host;

    if (!uniqueServicesMap.has(key)) {
      // Enhance the service with better device type detection before storing
      const enhancedService = enhanceDiscoveredService(service);
      uniqueServicesMap.set(key, enhancedService);
    }
  }

  const filteredServices = Array.from(uniqueServicesMap.values());

  // Main rendered UI, displays the devices found and the status of the link
  return (
    <div className={wrapperClassname}>
      {discoveredElementTitle}

      {/* Display message when no devices are found */}
      {!roomAgent && !machineAgent && discoveredServices.length === 0 && (
        <div className='mt-2 flex items-center justify-between rounded-4 bg-base-300 p-16 py-12'>
          <div className='text-sm font-medium'>No devices found</div>
          <Button variant='secondary' onClick={handleSearchAgain}>
            Search Again
          </Button>
        </div>
      )}

      {/* Display error messages */}
      {linkAgentError && !roomAgentSameAsMachine && !loading && (
        <Callout title={linkAgentError.message} severity='error' className='mb-8' />
      )}

      {/* Display discovered mDNS services */}
      {filteredServices.length > 0 && bridgeLinkDecision.allowed && (
        <div className='flex flex-col gap-8'>
          {filteredServices.map((service, index) => (
            <MDNSServiceEntry
              key={`${service.host}-${service.port}-${index}`}
              service={service}
              isPtzoptics={isPtzoptics}
              isConnecting={connectingServiceHost === service.host}
              error={serviceErrors[service.host] || null}
              handleConnect={() => handleConnectToService(service)}
              handleCancelLinkRequest={() => handleCancelLinkRequest(service)}
            />
          ))}
        </div>
      )}

      {/* Component/logic that allows another bridge to be linked */}
      {machineAgent && !roomAgentSameAsMachine && bridgeLinkDecision.allowed && (
        <DeviceEntry
          thisComputer
          name={machineAgent.name}
          agentId={machineAgent.id}
          agentSystemInfo={agentSystemInfo}
          selfDiscoerySystemInfo={selfDiscoerySystemInfo}
          linking={linking || unlinking}
          disabled={linking || unlinking}
          onLink={handleLinkBridgeElectron}
          error={linkError?.message}
          state='unlinked'
          isPtzoptics={isPtzoptics}
        />
      )}

      {/* Display connected agents */}
      {roomAgent &&
        !!agents.length &&
        agents.map((agent) => (
          <DeviceEntry
            key={agent.id}
            agentId={agent.id}
            thisComputer={agent.id === machineAgentId}
            name={agent.name}
            agentSystemInfo={agentSystemInfo}
            state='linked'
            isPtzoptics={isPtzoptics}
          />
        ))}
    </div>
  );
};

const promptUserToUnlink = async (_service: DiscoveredService, prompt: UsePrompt) => {
  const promise = new Promise((resolve) => {
    prompt.confirm({
      title: 'Unlink Bridge',
      message: [
        'This Bridge is currently linked to another Studio.',
        'By continuing, it will be unlinked from the existing Studio and linked with this one. This change cannot be undone.'
      ].join('\n'),
      okay: {
        variant: 'danger',
        label: 'Link Bridge with this Studio'
      },
      cancel: {
        label: 'Cancel'
      },
      onConfirm: (answer) => {
        return resolve(answer);
      }
    });
  });

  return await promise;
};

export const checkCanAutoUnlink = async (
  agentId: string | undefined,
  rooms: Record<string, RoomRecord>,
  token: string
) => {
  let needsUnlink = false;

  try {
    const roomId = await fetchRoomIdForAgentId((agentId ?? '') as Agent['id'], token);

    if (roomId === undefined) {
      throw new Error('Room ID not found');
    }

    const room = rooms[roomId];

    if (!room) {
      throw new Error('Room not found');
    }

    // check if we have permissions to link to this room
    if (room.permissions.canUpdate) {
      needsUnlink = true;
    }
  } catch (error) {
    console.error('Error fetching room ID for agent ID:', error);
  }

  return needsUnlink;
};
